/* EBONFORM — Product page (maison layout) behaviour */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (!root || root.dataset.pdpReady === '1') return;
    root.dataset.pdpReady = '1';

    /* Sticky offset = the part of the site header still visible on screen */
    const header = document.querySelector('[data-efh]');
    let hdrTick = false;
    const panel = root.querySelector('.pdp-info');
    const desktop = window.matchMedia('(min-width: 990px)');
    const setHeader = () => {
      hdrTick = false;
      const visible = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      root.style.setProperty('--hdr', `${visible}px`);
      /* Panel stays in view: pinned under the header when it fits the screen,
         otherwise it scrolls until its last line is visible and then holds. */
      if (panel && desktop.matches) {
        const room = window.innerHeight - visible;
        const h = panel.offsetHeight;
        root.style.setProperty('--sticky-top', `${h <= room ? visible : Math.round(window.innerHeight - h)}px`);
      }
    };
    const queueHeader = () => { if (!hdrTick) { hdrTick = true; requestAnimationFrame(setHeader); } };
    setHeader();
    window.addEventListener('scroll', queueHeader, { passive: true });
    window.addEventListener('resize', queueHeader, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueHeader);

    /* Load every gallery image once the page is ready so swiping never reveals a blank frame */
    window.addEventListener('load', () => {
      root.querySelectorAll('.pdp-slide__img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }, { once: true });

    /* ───────── Gallery ───────── */
    const track = root.querySelector('[data-pdp-track]');
    const slides = [...root.querySelectorAll('[data-pdp-slide]')];
    const current = root.querySelector('[data-pdp-current]');
    const progress = root.querySelector('[data-pdp-progress]');
    let index = 0;

    const syncPager = () => {
      if (current) current.textContent = String(index + 1);
      if (progress) progress.style.transform = `translateX(${index * 100}%)`;
    };

    const isCarousel = () => track && track.scrollWidth > track.clientWidth + 2;

    if (track && slides.length > 1) {
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          if (!isCarousel()) return;
          const i = Math.round(track.scrollLeft / track.clientWidth);
          const next = Math.max(0, Math.min(slides.length - 1, i));
          if (next !== index) { index = next; syncPager(); }
        });
      }, { passive: true });

      track.addEventListener('keydown', (e) => {
        if (!isCarousel()) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = Math.max(0, Math.min(slides.length - 1, index + (e.key === 'ArrowRight' ? 1 : -1)));
        track.scrollTo({ left: next * track.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
      });

      /* Keep the active slide aligned when the viewport changes (rotation, resize) */
      let lastW = track.clientWidth;
      const realign = () => {
        if (!isCarousel() || track.clientWidth === lastW) return;
        lastW = track.clientWidth;
        track.scrollLeft = index * track.clientWidth;
      };
      if ('ResizeObserver' in window) new ResizeObserver(realign).observe(track);
    }
    syncPager();

    /* ───────── Viewer ───────── */
    const viewer = root.querySelector('[data-pdp-viewer]');
    if (viewer) {
      const vImg = viewer.querySelector('[data-pdp-viewer-img]');
      const vStage = viewer.querySelector('[data-pdp-viewer-stage]');
      const vCount = viewer.querySelector('[data-pdp-viewer-count]');
      const sources = slides.map((s) => s.querySelector('.pdp-slide__img')).filter(Boolean);
      let v = 0;
      let lastFocus = null;

      const show = (i) => {
        if (!sources.length) return;
        v = (i + sources.length) % sources.length;
        const src = sources[v];
        viewer.classList.remove('is-zoomed');
        vImg.style.transformOrigin = '50% 50%';
        vImg.src = src.dataset.pdpFull || src.currentSrc || src.src;
        vImg.alt = src.alt || '';
        if (vCount) vCount.textContent = `${v + 1} / ${sources.length}`;
      };

      const open = (i) => {
        lastFocus = document.activeElement;
        show(i);
        if (typeof viewer.showModal === 'function') viewer.showModal(); else viewer.setAttribute('open', '');
        document.documentElement.style.overflow = 'hidden';
      };
      const close = () => { if (viewer.open) viewer.close(); };
      viewer.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        viewer.classList.remove('is-zoomed');
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      });

      root.querySelectorAll('[data-pdp-open]').forEach((btn) => {
        btn.addEventListener('click', () => open(Number(btn.dataset.pdpOpen) || 0));
      });
      viewer.querySelector('[data-pdp-viewer-close]')?.addEventListener('click', close);
      viewer.querySelector('[data-pdp-viewer-prev]')?.addEventListener('click', () => show(v - 1));
      viewer.querySelector('[data-pdp-viewer-next]')?.addEventListener('click', () => show(v + 1));
      viewer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') show(v + 1);
        if (e.key === 'ArrowLeft') show(v - 1);
      });

      /* Tap/click toggles zoom; while zoomed the pointer pans. Swipe navigates when not zoomed. */
      const originFrom = (e) => {
        const r = vStage.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
        return `${x}% ${y}%`;
      };
      let downX = 0, downY = 0, downT = 0, pointerDown = false, moved = false;
      vStage.addEventListener('pointerdown', (e) => {
        pointerDown = true; moved = false;
        downX = e.clientX; downY = e.clientY; downT = Date.now();
        if (viewer.classList.contains('is-zoomed')) viewer.classList.add('is-panning');
      });
      vStage.addEventListener('pointermove', (e) => {
        if (Math.abs(e.clientX - downX) > 6 || Math.abs(e.clientY - downY) > 6) moved = pointerDown || moved;
        if (viewer.classList.contains('is-zoomed') && (e.pointerType === 'mouse' || pointerDown)) {
          vImg.style.transformOrigin = originFrom(e);
        }
      });
      const end = (e) => {
        if (!pointerDown) return;
        pointerDown = false;
        viewer.classList.remove('is-panning');
        const dx = e.clientX - downX;
        const dy = e.clientY - downY;
        const zoomed = viewer.classList.contains('is-zoomed');
        if (!zoomed && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) && Date.now() - downT < 700) {
          show(v + (dx < 0 ? 1 : -1));
          return;
        }
        if (!moved || (zoomed && e.pointerType === 'mouse')) {
          if (zoomed) {
            viewer.classList.remove('is-zoomed');
          } else {
            vImg.style.transformOrigin = originFrom(e);
            viewer.classList.add('is-zoomed');
          }
        }
      };
      vStage.addEventListener('pointerup', end);
      vStage.addEventListener('pointercancel', () => { pointerDown = false; viewer.classList.remove('is-panning'); });
    }

    /* ───────── Drawers (story + information panels) ───────── */
    let drawerFocus = null;
    root.querySelectorAll('[data-pdp-drawer]').forEach((dlg) => {
      dlg.querySelector('[data-pdp-drawer-close]')?.addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
      dlg.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        if (drawerFocus && drawerFocus.focus) drawerFocus.focus({ preventScroll: true });
      });
    });
    root.querySelectorAll('[data-pdp-drawer-open]').forEach((btn) => {
      const dlg = root.querySelector(`[data-pdp-drawer="${btn.dataset.pdpDrawerOpen}"]`);
      if (!dlg) { btn.hidden = true; return; }
      btn.addEventListener('click', () => {
        drawerFocus = btn;
        dlg.showModal();
        dlg.querySelector('.pdp-drawer__panel')?.scrollTo(0, 0);
        document.documentElement.style.overflow = 'hidden';
      });
    });

    /* ───────── Variants (only rendered when a product has options) ───────── */
    const variantsEl = root.querySelector('[data-pdp-variants]');
    const idInput = root.querySelector('[data-pdp-variant-id]');
    const buyBtn = root.querySelector('[data-pdp-buy]');
    const buyLabel = root.querySelector('[data-pdp-buy-label]');
    const priceEl = root.querySelector('[data-pdp-price]');
    const availEl = root.querySelector('[data-pdp-avail]');
    if (variantsEl && idInput) {
      let variants = [];
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { variants = []; }
      const selects = [...root.querySelectorAll('[data-pdp-option]')];
      const money = (cents) => {
        try {
          const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PKR';
          return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
        } catch (e) { return (cents / 100).toFixed(2); }
      };
      const update = () => {
        const chosen = selects.map((s) => s.value);
        const match = variants.find((vr) => vr.options.every((o, i) => o === chosen[i]));
        if (!match) {
          if (buyBtn) { buyBtn.disabled = true; buyBtn.setAttribute('aria-disabled', 'true'); }
          if (buyLabel) buyLabel.textContent = 'Unavailable';
          return;
        }
        idInput.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price);
        if (buyBtn) {
          buyBtn.disabled = !match.available;
          buyBtn.toggleAttribute('aria-disabled', !match.available);
        }
        if (buyLabel) buyLabel.textContent = match.available ? 'Acquire this piece' : 'Edition sold out';
        if (availEl) availEl.innerHTML = match.available ? '<i aria-hidden="true"></i>Available for acquisition' : 'This edition is not currently available online.';
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url.toString());
      };
      selects.forEach((s) => s.addEventListener('change', update));
    }

    /* ───────── Add to cart ───────── */
    const form = root.querySelector('[data-pdp-form]');
    const errorEl = root.querySelector('[data-pdp-error]');
    if (form && buyBtn) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (buyBtn.disabled) return;
        const label = buyLabel ? buyLabel.textContent : '';
        buyBtn.disabled = true;
        buyBtn.setAttribute('aria-busy', 'true');
        if (buyLabel) buyLabel.textContent = 'Adding…';
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        try {
          const action = form.getAttribute('action') || '/cart/add';
          const res = await fetch(action.endsWith('.js') ? action : `${action}.js`, {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          const payload = await res.json().catch(() => ({}));
          if (!res.ok || payload.status) throw new Error(payload.description || payload.message || 'This piece could not be added to your cart.');
          window.location.assign(form.dataset.cartUrl || '/cart');
        } catch (err) {
          buyBtn.disabled = false;
          buyBtn.removeAttribute('aria-busy');
          if (buyLabel) buyLabel.textContent = label;
          if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        }
      });
    }
  };

  /* "More from the archive" rail arrows */
  const initMore = (root) => {
    const more = root.querySelector('[data-pdp-more]');
    if (!more) return;
    const rail = more.querySelector('[data-pdp-more-rail]');
    const prev = more.querySelector('[data-pdp-more-prev]');
    const next = more.querySelector('[data-pdp-more-next]');
    if (!rail || !prev || !next) return;
    const step = () => {
      const card = rail.querySelector('.pdp-card');
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : rail.clientWidth;
    };
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max;
    };
    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: reduceMotion() ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: reduceMotion() ? 'auto' : 'smooth' }));
    rail.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  const boot = () => document.querySelectorAll('[data-pdp]').forEach((root) => { init(root); if (root.dataset.pdpMore !== '1') { root.dataset.pdpMore = '1'; initMore(root); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
